/**
 * The transcript: committed messages plus the run in flight.
 *
 * Reads as a trace rather than a chat log. Tool activity is inline, in the
 * order it happened, in a timeline gutter, so it is legible as an agent
 * working rather than a bot typing.
 */
import { useLayoutEffect, useRef, useState } from "react";
import { AlertTriangle, ArrowDown, Check, ExternalLink } from "lucide-react";
import type { ChatMessageDto } from "@hydra-notes/shared";
import { getData } from "@/data/store";
import { openItem } from "@/commands/nav";
import type { RunState, ToolCall } from "@/store/chat";
import { Markdown } from "./Markdown";
import { metaFor, targetLabel, targetNoteId } from "./toolMeta";
import s from "./chat.module.css";

function noteTitle(id: string | null): string | null {
  if (!id) return null;
  try {
    return getData().workspace.getItem(id)?.title || "Untitled";
  } catch {
    return null;
  }
}

function ToolChip({ call }: { call: ToolCall }) {
  const [open, setOpen] = useState(false);
  const meta = metaFor(call.name);
  const Icon = meta.icon;
  const noteId = targetNoteId(call.input);
  const label = noteTitle(noteId) ?? targetLabel(call.input);
  const failed = call.status === "error";

  return (
    <div className={[s.chipWrap, meta.write ? s.chipWrite : "", failed ? s.chipFailed : ""].join(" ")}>
      <button type="button" className={s.chip} onClick={() => setOpen(!open)}>
        <Icon size={12} className={s.chipIcon} />
        <span className={s.chipVerb}>{call.status === "running" ? meta.running : meta.done}</span>
        <span className={s.chipTarget}>{label}</span>
        <span className={s.chipSpacer} />
        {call.status === "running" ? <span className={s.runDot} /> : null}
        {call.status === "ok" ? <Check size={12} className={s.chipOk} /> : null}
        {failed ? <AlertTriangle size={12} className={s.chipErr} /> : null}
        {call.durationMs ? <span className={s.chipTime}>{(call.durationMs / 1000).toFixed(1)}s</span> : null}
      </button>
      {meta.write && noteId && call.status === "ok" ? (
        <button type="button" className={s.chipOpen} onClick={() => openItem(noteId)}>
          <ExternalLink size={11} /> Open
        </button>
      ) : null}
      {open || failed ? (
        <div className={s.chipBody}>
          <div className={s.chipLabel}>Input</div>
          <pre className={s.chipPre}>{JSON.stringify(call.input, null, 2)}</pre>
          {call.result ? (
            <>
              <div className={s.chipLabel}>Result{call.truncated ? " (truncated by the server)" : ""}</div>
              <pre className={s.chipPre}>{call.result.slice(0, 2000)}</pre>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Rebuild tool calls from persisted rows so a reload still shows the trace. */
function toolsFromMessages(messages: ChatMessageDto[]): Map<string, ToolCall> {
  const map = new Map<string, ToolCall>();
  for (const m of messages) {
    if (m.kind === "tool_use" && m.toolUseId) {
      let input: unknown = {};
      try {
        input = JSON.parse(m.body);
      } catch {
        input = {};
      }
      map.set(m.toolUseId, { id: m.toolUseId, name: m.toolName ?? "", input, status: "running" });
    }
    if (m.kind === "tool_result" && m.toolUseId) {
      const existing = map.get(m.toolUseId);
      if (existing) {
        existing.status = m.isError ? "error" : "ok";
        existing.result = m.body;
        existing.truncated = m.truncated;
      }
    }
  }
  return map;
}

export function Transcript({ messages, run }: { messages: ChatMessageDto[]; run: RunState | undefined }) {
  const ref = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const [showJump, setShowJump] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [messages.length, run?.text, run?.tools.length]);

  const tools = toolsFromMessages(messages);
  const rendered = new Set<string>();

  return (
    <div className={s.transcriptWrap}>
      <div
        className={[s.transcript, "u-selectable"].join(" ")}
        ref={ref}
        onScroll={(e) => {
          const el = e.currentTarget;
          stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
          setShowJump(!stick.current);
        }}
      >
        {messages.map((m) => {
          if (m.kind === "tool_result") return null;
          if (m.kind === "tool_use") {
            if (!m.toolUseId || rendered.has(m.toolUseId)) return null;
            rendered.add(m.toolUseId);
            const call = tools.get(m.toolUseId);
            return call ? <ToolChip key={m.id} call={call} /> : null;
          }
          if (m.role === "user") {
            return (
              <div key={m.id} className={s.userTurn}>
                <div className={s.turnLabel}>You</div>
                <div className={s.userText}>{m.body}</div>
              </div>
            );
          }
          return (
            <div key={m.id} className={s.assistantTurn}>
              <Markdown source={m.body} />
              {m.outputTokens ? (
                <div className={s.usage}>
                  {m.inputTokens.toLocaleString()} in / {m.outputTokens.toLocaleString()} out
                </div>
              ) : null}
            </div>
          );
        })}

        {run ? (
          <div className={s.assistantTurn}>
            {run.tools.map((t) => (
              <ToolChip key={t.id} call={t} />
            ))}
            {run.text ? <Markdown source={run.text} /> : null}
            {run.notice ? <div className={s.notice}>{run.notice}</div> : null}
            {run.error ? <div className={s.errorBox}>{run.error}</div> : null}
            {!run.text && run.tools.length === 0 && !run.error ? <div className={s.thinking}>Thinking</div> : null}
          </div>
        ) : null}
      </div>
      {showJump ? (
        <button
          type="button"
          className={s.jump}
          onClick={() => {
            const el = ref.current;
            if (el) el.scrollTop = el.scrollHeight;
            stick.current = true;
            setShowJump(false);
          }}
        >
          <ArrowDown size={12} /> Jump to latest
        </button>
      ) : null}
    </div>
  );
}
